import { FirebaseError } from '@firebase/util';
import { kebabCase } from 'lodash';
import { useCallback, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import { useAppContext } from '../../../data/context/app-context';
import { FormMessage, FormMessageType } from '../../../data/utils/form.utils';
import {
  Button,
  ButtonIcon,
  ButtonType,
} from '../../../elements/button/button';
import { InputFieldController } from '../../../elements/form/controllers/input-field';
import { FormColumns, FormRow } from '../../../elements/form/form';
import { CustomFormattedMessage } from '../../../elements/intl/custom-formatted-message';
import { Spinner } from '../../../elements/spinner/spinner';
import { AuthStatus } from '../../../firebase/authentication';
import { useLoginForm } from './form/use-login-form';

export function Login() {
  const intl = useIntl();

  const { validating, signIn } = useAppContext();

  const navigate = useNavigate();

  const { form, formIsValid } = useLoginForm();

  const [loading, setLoading] = useState<boolean>(false);

  const [formMessage, setFormMessage] = useState<FormMessage | null>(null);

  const onHandleSubmit = useCallback(async () => {
    form.handleSubmit(async () => {
      setLoading(true);

      setFormMessage(null);

      const values = form.getValues();

      try {
        const result = await signIn(values.email, values.password);

        if (result.status === AuthStatus.UNAUTHENTICATED) {
          setFormMessage({
            type: FormMessageType.ERROR,
            message: intl.formatMessage({
              id: `common.errors.${kebabCase(
                result.message ?? 'unknown-error'
              )}`,
              defaultMessage: result.message ?? 'unknown-error',
            }),
          });
        } else {
          navigate('/pincode');
        }
      } catch (error) {
        if (error instanceof FirebaseError) {
          setFormMessage({
            type: FormMessageType.ERROR,
            message: intl.formatMessage({
              id: `common.errors.${kebabCase(error.code ?? 'unknown-error')}`,
              defaultMessage: error.code ?? 'unknown-error',
            }),
          });
        } else {
          setFormMessage({
            type: FormMessageType.ERROR,
            message: (error as Error).message,
          });
        }
      }

      setLoading(false);
    })();
  }, [intl, form, signIn, navigate]);

  return validating ? (
    <Spinner />
  ) : (
    <FormProvider {...form}>
      {formMessage?.type === FormMessageType.ERROR && (
        <p className="form-error">{formMessage.message}</p>
      )}

      <form onSubmit={onHandleSubmit}>
        <FormRow>
          <InputFieldController
            testId="login-email"
            name="email"
            type="email"
            label={<CustomFormattedMessage id="common.email" />}
            placeholder={intl.formatMessage({ id: 'common.email' })}
          />
        </FormRow>

        <FormRow>
          <InputFieldController
            testId="login-password"
            type="password"
            name="password"
            label={<CustomFormattedMessage id="common.password" />}
            placeholder={intl.formatMessage({ id: 'common.password' })}
          />
        </FormRow>

        <FormColumns>
          <FormRow>
            <Button
              testId="forgot-password"
              type={ButtonType.TEXT}
              onClick={() => {
                navigate('/password-forgot');
              }}
            >
              <CustomFormattedMessage id="auth.forgot-password" />
            </Button>
          </FormRow>

          <FormRow>
            <Button
              testId="login"
              useAsSubmit
              type={ButtonType.SECONDARY}
              icon={loading ? ButtonIcon.LOADING : ButtonIcon.NONE}
              disabled={!formIsValid || loading}
              onClick={onHandleSubmit}
            >
              <CustomFormattedMessage id="common.login" />
            </Button>
          </FormRow>
        </FormColumns>
      </form>
    </FormProvider>
  );
}
